import { useCallback, useState } from "react";

export interface User {
  auth: UserAuth;
  serviceBaseUrl: string | null;
}

export interface UserAuth {
  authSession: string | null;
  isUserLoggedIn: boolean;
  setAuthSession: (sessionId: string) => void;
}

/**
 * This hook gets the sessionId if present from the sessionStorage.
 * It also exposes a callback `setAuthSessionToStorage` which can be used to
 * set the authSession and loggedIn states of the user. It can take a string as an argument,
 * which can be empty if sessionId is undefined.
 * @returns auth session object.
 */
export const useAuth: () => UserAuth = () => {
  // Retain the logged in sessionId for the user session on refresh.
  const sessionId = sessionStorage.getItem("user.sessionId") ?? null;

  const [{ authSession, isUserLoggedIn }, setAuthSession] = useState({
    authSession: sessionId,
    isUserLoggedIn: !!sessionId,
  });

  const setAuthSessionToStorage = useCallback((userSessionId: string) => {
    sessionStorage.setItem("user.sessionId", userSessionId);
    setAuthSession({
      authSession: userSessionId,
      isUserLoggedIn: !!userSessionId,
    });
  }, []);

  return {
    authSession,
    isUserLoggedIn,
    setAuthSession: setAuthSessionToStorage,
  };
};
